import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import LogoSvg from "../Logo"
import { contactsApi, type ContactStore, type SocialLink } from '@/api/endpoints'
// import StoreSupportContacts from '@/components/StoreSupportContacts'
import { GlobalSocialLinkBadge } from '@/components/GlobalSocialLinkBadge'
import { GoLinkExternal } from 'react-icons/go'
import { rawPhoneHref } from '@/utils/storeSupportContact'
import { isRestrictedSocialLink, isRestrictedSupportContact } from '@/utils/restrictedSocials'

const compareStores = (a: ContactStore, b: ContactStore) => {
  const aOrder = a.order ?? Number.MAX_SAFE_INTEGER
  const bOrder = b.order ?? Number.MAX_SAFE_INTEGER
  return aOrder - bOrder || a.id - b.id
}

const Footer = () => {
  const currentYear = new Date().getFullYear()
  const [stores, setStores] = useState<ContactStore[]>([])
  const [socials, setSocials] = useState<SocialLink[]>([])

  useEffect(() => {
    contactsApi
      .getContacts()
      .then((data) => {
        setStores(
          [...(data.stores ?? [])]
            .sort(compareStores)
            .map((store) => ({
              ...store,
              socials: (store.socials ?? []).filter((social) => !isRestrictedSocialLink(social)),
              support_contacts: (store.support_contacts ?? []).filter(
                (contact) => !isRestrictedSupportContact(contact)
              ),
            }))
        )
        setSocials((data.socials ?? []).filter((social) => !isRestrictedSocialLink(social)))
      })
      .catch(() => {
        setStores([])
        setSocials([])
      })
  }, [])

  const documents = [
    { title: 'Пользовательское соглашение', path: '/legal/rules' },
    { title: 'Политика персональных данных', path: '/legal/privacy' },
    { title: 'Политика cookie', path: '/legal/cookies' },
    { title: 'Публичная оферта', path: '/legal/offer' },
    { title: 'Оплата, доставка и возврат', path: '/legal/payment-delivery' },
    { title: 'Согласие на обработку ПД', path: '/legal/personal-data-consent' },
    { title: 'Согласие на рассылки', path: '/legal/marketing-consent' },
    { title: 'Согласие на публикацию отзыва', path: '/legal/review-consent' },
    { title: 'Сведения о продавце', path: '/legal/seller-details' },
  ]

  return (
    <footer className="bg-footer-color text-gray-300 mt-12 pb-20 lg:pb-0">
      <div className="container mx-auto px-4 py-12">
        <div className="flex flex-col lg:flex-row gap-10">
          {/* Logo & Social */}
          <div className="lg:col-span-1">
            <a href="/" className="inline-block text-primary font-bold text-xl">
              <LogoSvg className="w-24.25 text-white" />
            </a>
            <p className="mt-4 text-sm">Мы в соцсетях</p>
            <div className="flex flex-col gap-2 mt-2">
              {socials.map((social) => (
                <GlobalSocialLinkBadge key={social.id} social={social} />
              ))}
            </div>
          </div>

          {/* Контакты */}
          <div>
            <p className="font-bold text-white mb-3">Контакты</p>

            <div className="flex flex-col space-y-2">
              {stores.map((store) => (
                <ul key={store.id} className="text-sm flex flex-col gap-y-1 text-wrap lg:text-nowrap">
                  <li>
                    <Link
                      to={`/stores/${store.id}`}
                      className="text-white hover:text-primary transition-colors flex flex-row items-center gap-1 text-base"
                    >
                      {store.title} <GoLinkExternal />
                    </Link>
                  </li>
                  {store.addresses[0] ? (
                    <li className="text-gray-400">{store.addresses[0]}</li>
                  ) : null}
                  {store.phones.map((phone, phoneIndex) => (
                    <li key={`${store.id}-phone-${phoneIndex}-${phone}`}>
                      <a
                        href={rawPhoneHref(phone)}
                        rel="noreferrer"
                        className="text-primary"
                      >
                        {phone}
                      </a>
                    </li>
                  ))}
                  {/* {store.support_contacts && store.support_contacts.length > 0 ? (
                    <li className="pt-1">
                      <StoreSupportContacts
                        contacts={store.support_contacts}
                        labelClassName="text-gray-400"
                        nameClassName="text-gray-300"
                        linkClassName="text-primary hover:underline lowercase"
                      />
                    </li>
                  ) : null} */}
                </ul>
              ))}
            </div>
          </div>

          <div className=''>
            <p className="font-bold text-white mb-3">Документы</p>
            <ul className="space-y-1.5 text-sm">
              {documents.map((doc) => (
                <li key={doc.path}>
                  <Link to={doc.path} className="text-white hover:text-primary transition-colors flex flex-row items-center gap-1 text-base">
                    {doc.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Интернет-магазин */}
          {/* <div>
            <h3 className="font-bold text-white mb-3">Интернет-магазин</h3>
            <ul className="space-y-1.5 text-sm">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Каталог товаров
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Бренды
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Поступления
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Акции
                </a>
              </li>
            </ul>
          </div> */}

          {/* Компания */}
          {/* <div>
            <h3 className="font-bold text-white mb-3">Компания</h3>
            <ul className="space-y-1.5 text-sm">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  О нас
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Вакансии
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Контакты
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Отзывы
                </a>
              </li>
            </ul>
          </div> */}

          {/* Помощь */}
          {/* <div>
            <h3 className="font-bold text-white mb-3">Помощь</h3>
            <ul className="space-y-1.5 text-sm">
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Условия оплаты
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Условия доставки
                </a>
              </li>
              <li>
                <a href="#" className="hover:text-white transition-colors">
                  Гарантия на товар
                </a>
              </li>
            </ul>
          </div> */}
        </div>

        <div className="mt-8 pt-6 border-t border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-gray-500">
            © {currentYear} • NurM. Все права защищены.
          </p>
        </div>
      </div>
    </footer>
  )
}

export default Footer
